import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import type { ProfileAreaSection, ProfileTopicRow, SubScore } from '@app/src/pages/SustainabilityProfile/profileLogic'
import { sustainabilityAreaTranslationKeys } from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskAreaCard'
import SustainabilityAreaIcon from '@app/src/pages/TopicalRisk/SustainabilityAreaIcon'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import {
  Box,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import type { AssessmentLinkResolver } from '../assessmentLinks'
import DeltaValue from './DeltaValue'
import { POSITIVE_COLOR } from './profileColors'
import TopicScoreBar from './TopicScoreBar'

interface TopicPerformanceTableProps {
  sections: ProfileAreaSection[]
  filterLabel: string
  showOverallRisk: boolean
  screeningTemplateIds?: Set<number>
  resolveLink: AssessmentLinkResolver
}

const EMPTY_SCREENING_TEMPLATE_IDS: Set<number> = new Set()

const COLUMN_WIDTH = {
  expand: 56,
  topic: 300,
  numeric: 88,
  overallRisk: 128,
} as const

const NUMERIC_COLUMN_COUNT = 3

const ExpandCell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <TableCell padding="none" align="center">
    {children}
  </TableCell>
)

const RiskCell: React.FC<{ row?: ProfileTopicRow }> = ({ row }) => (
  <TableCell align="right">{row?.riskStatus !== undefined && <SimpleRiskChip riskStatus={row.riskStatus} />}</TableCell>
)

const NumericCells: React.FC<{ sub: SubScore }> = ({ sub }) => (
  <>
    <TableCell>
      <Typography variant="subtitle1">{sub.score ?? '—'}</Typography>
    </TableCell>
    <TableCell>
      <Typography variant="subtitle1" color="text.secondary">
        {sub.peerMean ?? '—'}
      </Typography>
    </TableCell>
    <TableCell>
      <DeltaValue delta={sub.delta} positiveColor={POSITIVE_COLOR} />
    </TableCell>
  </>
)

const LoadingCells: React.FC = () => (
  <>
    <TableCell>
      <Skeleton variant="rounded" height={8} />
    </TableCell>
    {Array.from({ length: NUMERIC_COLUMN_COUNT }, (_, key) => (
      <TableCell key={key}>
        <Skeleton variant="text" width={28} />
      </TableCell>
    ))}
  </>
)

const ScoreBarCell: React.FC<{ sub: SubScore; link?: string }> = ({ sub, link }) => {
  if (sub.score === undefined) return <TableCell />
  const bar = <TopicScoreBar score={sub.score} peerMean={sub.peerMean} />
  return (
    <TableCell>
      {link ? (
        <Link component={RouterLink} to={link} tabIndex={-1} aria-hidden sx={{ display: 'block' }}>
          {bar}
        </Link>
      ) : (
        bar
      )}
    </TableCell>
  )
}

const TopicName: React.FC<{ name: string; link?: string }> = ({ name, link }) =>
  link ? (
    <Link component={RouterLink} to={link} variant="subtitle1" color="text.primary" underline="hover">
      {name}
    </Link>
  ) : (
    <Typography variant="subtitle1">{name}</Typography>
  )

const AreaRow: React.FC<{ area: ProfileAreaSection['area']; colSpan: number }> = ({ area, colSpan }) => {
  const { formatMessage } = useIntl()
  return (
    <TableRow sx={{ bgcolor: 'grey.100', '& td': { height: 56 } }}>
      <ExpandCell>
        <SustainabilityAreaIcon area={area} sx={{ display: 'block', mx: 'auto' }} />
      </ExpandCell>
      <TableCell colSpan={colSpan - 1}>
        <Typography variant="subtitle1">{formatMessage({ id: sustainabilityAreaTranslationKeys[area] })}</Typography>
      </TableCell>
    </TableRow>
  )
}

const NoDataRow: React.FC<{ row: ProfileTopicRow; link?: string; showOverallRisk: boolean }> = ({
  row,
  link,
  showOverallRisk,
}) => {
  const { formatMessage } = useIntl()
  return (
    <TableRow sx={{ bgcolor: 'grey.50' }}>
      <ExpandCell />
      <TableCell>
        <TopicName name={row.name} link={link} />
      </TableCell>
      <TableCell colSpan={1 + NUMERIC_COLUMN_COUNT}>
        <Typography variant="body1" color="text.disabled">
          {formatMessage({ id: 'sustainabilityProfile.topics.noData' })}
        </Typography>
      </TableCell>
      {showOverallRisk && <RiskCell row={row} />}
    </TableRow>
  )
}

const SectionSubRow: React.FC<{ label: string; sub: SubScore; showOverallRisk: boolean }> = ({
  label,
  sub,
  showOverallRisk,
}) => (
  <TableRow sx={{ bgcolor: 'grey.50' }}>
    <ExpandCell />
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </TableCell>
    <ScoreBarCell sub={sub} />
    <NumericCells sub={sub} />
    {showOverallRisk && <RiskCell />}
  </TableRow>
)

const TopicRow: React.FC<{
  row: ProfileTopicRow
  showOverallRisk: boolean
  screeningTemplateIds: Set<number>
  resolveLink: AssessmentLinkResolver
}> = ({ row, showOverallRisk, screeningTemplateIds, resolveLink }) => {
  const { formatMessage } = useIntl()
  const [expanded, setExpanded] = useState(false)

  const link =
    row.assessmentTemplateId === undefined ? undefined : resolveLink(row.assessmentTemplateId, row.periodName)
  const isScreening = row.assessmentTemplateId !== undefined && screeningTemplateIds.has(row.assessmentTemplateId)
  const hasScore = row.overall.score !== undefined

  if (!hasScore && !isScreening) return <NoDataRow row={row} link={link} showOverallRisk={showOverallRisk} />

  return (
    <>
      <TableRow>
        <ExpandCell>
          {row.hasSectionBreakdown && (
            <IconButton
              onClick={() => setExpanded(prev => !prev)}
              aria-label={formatMessage({ id: 'sustainabilityProfile.topics.expandAria' })}
              aria-expanded={expanded}
            >
              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </ExpandCell>
        <TableCell>
          <Stack minWidth={0}>
            <TopicName name={row.name} link={link} />
            {hasScore && (
              <Typography variant="caption" color="text.secondary">
                {formatMessage({
                  id: row.isScreened
                    ? 'sustainabilityProfile.dataSource.aiScreened'
                    : 'sustainabilityProfile.dataSource.selfReported',
                })}
              </Typography>
            )}
          </Stack>
        </TableCell>
        {hasScore ? (
          <>
            <ScoreBarCell sub={row.overall} link={link} />
            <NumericCells sub={row.overall} />
          </>
        ) : (
          <LoadingCells />
        )}
        {showOverallRisk && <RiskCell row={row} />}
      </TableRow>
      {expanded && row.ownOps.score !== undefined && (
        <SectionSubRow
          label={formatMessage({ id: 'sustainabilityProfile.topics.subRow.ownOperations' })}
          sub={row.ownOps}
          showOverallRisk={showOverallRisk}
        />
      )}
      {expanded && row.supplyChain.score !== undefined && (
        <SectionSubRow
          label={formatMessage({ id: 'sustainabilityProfile.topics.subRow.supplyChain' })}
          sub={row.supplyChain}
          showOverallRisk={showOverallRisk}
        />
      )}
    </>
  )
}

const TopicPerformanceTable: React.FC<TopicPerformanceTableProps> = ({
  sections,
  filterLabel,
  showOverallRisk,
  screeningTemplateIds = EMPTY_SCREENING_TEMPLATE_IDS,
  resolveLink,
}) => {
  const { formatMessage } = useIntl()
  const columnCount = 3 + NUMERIC_COLUMN_COUNT + (showOverallRisk ? 1 : 0)

  return (
    <Box border={1} borderColor="divider" borderRadius={2} overflow="hidden" bgcolor="background.paper">
      <Table
        sx={{
          tableLayout: 'fixed',
          '& td, & th': { py: 1.5, borderColor: 'divider' },
          '& tbody tr:last-of-type td': { borderBottom: 0 },
        }}
      >
        <TableHead>
          <TableRow sx={theme => ({ '& th': { ...theme.typography.caption, height: 40, borderBottom: 0 } })}>
            <TableCell width={COLUMN_WIDTH.expand} padding="none" />
            <TableCell width={COLUMN_WIDTH.topic}>
              {formatMessage({ id: 'sustainabilityProfile.topics.columns.topic' })}
            </TableCell>
            <TableCell>
              {formatMessage(
                { id: 'sustainabilityProfile.topics.columns.scoreVsBenchmark' },
                { filter: filterLabel, u: (chunks: React.ReactNode) => <u>{chunks}</u> },
              )}
            </TableCell>
            <TableCell width={COLUMN_WIDTH.numeric}>
              {formatMessage({ id: 'sustainabilityProfile.topics.columns.score' })}
            </TableCell>
            <TableCell width={COLUMN_WIDTH.numeric}>
              {formatMessage({ id: 'sustainabilityProfile.topics.columns.peers' })}
            </TableCell>
            <TableCell width={COLUMN_WIDTH.numeric}>
              {formatMessage({ id: 'sustainabilityProfile.topics.columns.vsPeers' })}
            </TableCell>
            {showOverallRisk && (
              <TableCell align="right" width={COLUMN_WIDTH.overallRisk}>
                {formatMessage({ id: 'sustainabilityProfile.topics.columns.overallRisk' })}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map(section => (
            <React.Fragment key={section.area}>
              <AreaRow area={section.area} colSpan={columnCount} />
              {section.rows.map(row => (
                <TopicRow
                  key={row.topicId}
                  row={row}
                  showOverallRisk={showOverallRisk}
                  screeningTemplateIds={screeningTemplateIds}
                  resolveLink={resolveLink}
                />
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default TopicPerformanceTable
